/**
 * Normaliza un texto para búsquedas: minúsculas y sin tildes/diacríticos.
 * Así "laminas" coincide con "Láminas" y viceversa.
 */
export function normalizar(texto: string | null | undefined): string {
  return (texto ?? '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '');
}

/**
 * Formatea un monto en bolivianos.
 * Si tiene parte decimal: "Bs 1.234,50". Si es entero: "Bs 1.234".
 */
export function formatBs(amount: number, opts: { sign?: boolean } = {}): string {
  const isInt = Number.isInteger(amount);
  const fmt = new Intl.NumberFormat('es-BO', {
    minimumFractionDigits: isInt ? 0 : 2,
    maximumFractionDigits: isInt ? 0 : 2,
  });
  const sign = opts.sign && amount > 0 ? '+' : '';
  return `${sign}Bs ${fmt.format(amount)}`;
}

export function formatNumber(n: number): string {
  return new Intl.NumberFormat('es-BO').format(n);
}

export function formatDate(d: Date | null | undefined): string {
  if (!d) return '—';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
}

export function formatDateTime(d: Date | null | undefined): string {
  if (!d) return '—';
  const hh = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  return `${formatDate(d)} ${hh}:${min}`;
}

export function diasHasta(fecha: Date | null | undefined): number | null {
  if (!fecha) return null;
  const ms = fecha.getTime() - Date.now();
  return Math.ceil(ms / (1000 * 60 * 60 * 24));
}

/**
 * Recibe un número en cualquier formato y devuelve el formato E.164 con prefijo Bolivia (591).
 * Ej: "60008504" -> "59160008504"; "+591 60008504" -> "59160008504".
 */
export function normalizarWhatsappBO(input: string): string {
  const digits = input.replace(/\D/g, '');
  if (digits.startsWith('591')) return digits;
  return `591${digits}`;
}

export function formatWhatsappBO(input: string): string {
  const num = normalizarWhatsappBO(input).slice(3);
  return `+591 ${num}`;
}

/**
 * Formatea un input de fecha mientras el usuario escribe.
 * Recibe lo que escribió (números o con / opcionales) y devuelve "DD/MM/YYYY"
 * con barras automáticas y máximo 10 chars.
 *
 * Ej: "3" → "3", "31" → "31", "3105" → "31/05", "31052026" → "31/05/2026"
 */
export function formatearFechaInput(input: string): string {
  const digits = input.replace(/\D/g, '').slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
}

/**
 * Parsea un string "DD/MM/YYYY" a Date. Devuelve null si es inválido o incompleto.
 */
export function parsearFechaDDMMYYYY(input: string): Date | null {
  const m = input.trim().match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!m) return null;
  const dd = Number(m[1]);
  const mm = Number(m[2]);
  const yyyy = Number(m[3]);
  if (mm < 1 || mm > 12 || dd < 1 || dd > 31 || yyyy < 1900 || yyyy > 2100) return null;
  const d = new Date(yyyy, mm - 1, dd);
  // Validar que no haya rebote (ej: 31/02/2026 → 03/03/2026)
  if (d.getFullYear() !== yyyy || d.getMonth() !== mm - 1 || d.getDate() !== dd) return null;
  return d;
}

/** Convierte un Date a "DD/MM/YYYY". */
export function dateToDDMMYYYY(d: Date | null | undefined): string {
  if (!d) return '';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd}/${mm}/${d.getFullYear()}`;
}

export function whatsappLink(numero: string, mensaje?: string): string {
  const clean = normalizarWhatsappBO(numero);
  const params = mensaje ? `?text=${encodeURIComponent(mensaje)}` : '';
  return `https://wa.me/${clean}${params}`;
}
